import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Search,
  Eye,
  FileText,
  UserCheck,
  Settings,
  Lock,
  History,
  Share2,
  ListChecks,
  UserX,
  RefreshCw,
  Mail,
  Globe,
  Copy,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Calendar,
  Check,
  AlertTriangle
} from "lucide-react";
import Footer from "../components/Footer";
import "./privacypolicy.css";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("sec1");
  const [copied, setCopied] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 13 Sections data for Table of Contents
  const sections = [
    { id: "sec1", number: "1", title: "Information We Collect" },
    { id: "sec2", number: "2", title: "How We Use Your Info" },
    { id: "sec3", number: "3", title: "Invoice & Business Data" },
    { id: "sec4", number: "4", title: "Account Information" },
    { id: "sec5", number: "5", title: "Cookies & Analytics" },
    { id: "sec6", number: "6", title: "Third-Party Services" },
    { id: "sec7", number: "7", title: "Data Security" },
    { id: "sec8", number: "8", title: "Data Retention" },
    { id: "sec9", number: "9", title: "Sharing of Information" },
    { id: "sec10", number: "10", title: "Your Rights" },
    { id: "sec11", number: "11", title: "Children's Privacy" },
    { id: "sec12", number: "12", title: "Changes to Policy" },
    { id: "sec13", number: "13", title: "Contact Us" }
  ];

  // Dynamic scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth"
      });
      setActiveSection(id);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("support@fastinvoice.in");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="privacy-policy-page">

      {/* GLOW DECORATIONS */}
      <div className="bg-glow glow-top-right"></div>
      <div className="bg-glow glow-bottom-left"></div>

      {/* HERO BANNER */}
      <section className="policy-hero">
        <div className="policy-hero-content">
          <div className="gst-badge">
            <span className="badge-dot"></span> Made for Indian Businesses • GST Ready
          </div>
          <h1>Privacy Policy</h1>
          <p className="hero-subtext">
            We value your privacy and are committed to protecting your personal information.
          </p>
          <div className="meta-info">
            <div className="meta-item">
              <Calendar size={16} />
              <span>Effective Date: <strong>June 29, 2026</strong></span>
            </div>
            <div className="meta-item">
              <ShieldCheck size={16} />
              <span>Security Protocols: <strong>Active & Confirmed</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT SECTION WITH SIDEBAR */}
      <main className="policy-main-container">
        <div className="policy-layout">
          
          {/* LEFT SIDEBAR - TABLE OF CONTENTS */}
          <aside className="policy-sidebar">
            <div className="sidebar-sticky-wrapper">
              <h3>Privacy Outline</h3>
              <ul className="toc-list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      className={`toc-link ${activeSection === section.id ? "active" : ""}`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="toc-num">{section.number}</span>
                      <span className="toc-title">{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="sidebar-cta">
                <h4>Privacy Questions?</h4>
                <p>Learn more about how we protect your customer data profiles.</p>
                <a href="mailto:support@fastinvoice.in" className="cta-email-btn">
                  <Mail size={16} />
                  Email Support
                </a>
              </div>
            </div>
          </aside>

          {/* RIGHT COLUMN - POLICY DETAILS */}
          <article className="policy-content">
            
            {/* INTRO */}
            <div className="policy-intro-card">
              <p>
                At <strong>FastInvoice</strong>, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website, mobile application, and related services. By using FastInvoice, you agree to the practices described in this Privacy Policy.
              </p>
            </div>

            {/* SECTION 1: Information We Collect */}
            <section id="sec1" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Search className="sec-icon" />
                </div>
                <h2>1. Information We Collect</h2>
              </div>
              <p>
                We may collect the following information:
              </p>
              
              <div className="info-collection-grid">
                <div className="info-chip"><Check size={14} /> Name</div>
                <div className="info-chip"><Check size={14} /> Email Address</div>
                <div className="info-chip"><Check size={14} /> Phone Number (if provided)</div>
                <div className="info-chip"><Check size={14} /> Business Name & Address</div>
                <div className="info-chip"><Check size={14} /> GST/VAT Information</div>
                <div className="info-chip"><Check size={14} /> Customer Details entered by you</div>
                <div className="info-chip"><Check size={14} /> Invoice & Billing Information</div>
                <div className="info-chip"><Check size={14} /> Device Info & IP Address</div>
                <div className="info-chip"><Check size={14} /> App Usage Analytics</div>
              </div>
            </section>

            {/* SECTION 2: How We Use Your Information */}
            <section id="sec2" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Eye className="sec-icon" />
                </div>
                <h2>2. How We Use Your Information</h2>
              </div>
              <p>
                Your information is used to:
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator"></span>
                  Create, manage, and share invoices.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Store your business information for auto-completion.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Provide support channels and customer help desk access.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Analyze usage trends to improve products and services.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Maintain system account security.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Synchronize your data across devices (where applicable).
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  Comply with applicable legal regulatory obligations.
                </li>
              </ul>
            </section>

            {/* SECTION 3: Invoice & Business Data */}
            <section id="sec3" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <FileText className="sec-icon" />
                </div>
                <h2>3. Invoice & Business Data</h2>
              </div>
              <p>
                All invoice information, customer details, and business information entered into FastInvoice remain your property.
              </p>
              <div className="highlight-callout teal-callout">
                <p>
                  We only process this information to provide the requested services. We never review, sell, or disclose your corporate invoicing transactions.
                </p>
              </div>
            </section>

            {/* SECTION 4: Account Information */}
            <section id="sec4" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <UserCheck className="sec-icon" />
                </div>
                <h2>4. Account Information</h2>
              </div>
              <p>
                If you create an account, we store your account information securely to enable login, cloud backup, synchronization, and account recovery.
              </p>
            </section>

            {/* SECTION 5: Cookies & Analytics */}
            <section id="sec5" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Settings className="sec-icon" />
                </div>
                <h2>5. Cookies & Analytics</h2>
              </div>
              <p>
                Our website and application may use cookies and analytics technologies to improve performance, understand user behavior, and enhance user experience.
              </p>
              <p className="subtext-para">
                You may disable cookies through your browser settings, although some features may not function properly.
              </p>
            </section>

            {/* SECTION 6: Third-Party Services */}
            <section id="sec6" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Globe className="sec-icon" />
                </div>
                <h2>6. Third-Party Services</h2>
              </div>
              <p>
                FastInvoice may use trusted third-party services including:
              </p>
              <div className="providers-grid">
                <div className="provider-card">
                  <h4>Google Firebase</h4>
                  <p>Secure databases, user authentication, and cloud storage hosting.</p>
                </div>
                <div className="provider-card">
                  <h4>Google Analytics</h4>
                  <p>Tracking app crashes, page visits, and session patterns.</p>
                </div>
                <div className="provider-card">
                  <h4>Google Play Services</h4>
                  <p>Delivering notifications and mobile OS integrations.</p>
                </div>
                <div className="provider-card">
                  <h4>Cloud Storage</h4>
                  <p>Storing invoice drafts, assets, and backups securely.</p>
                </div>
                <div className="provider-card">
                  <h4>Email Providers</h4>
                  <p>Processing transactional invoice mailings and receipts.</p>
                </div>
              </div>
              <div className="alert-box info-alert">
                These services may collect information according to their own privacy policies.
              </div>
            </section>

            {/* SECTION 7: Data Security */}
            <section id="sec7" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Lock className="sec-icon" />
                </div>
                <h2>7. Data Security</h2>
              </div>
              <p>
                We implement appropriate technical and organizational security measures to protect your information against unauthorized access, disclosure, alteration, or destruction.
              </p>
              <div className="alert-box note-alert">
                <strong>Please Note:</strong> No internet-based service can guarantee complete security.
              </div>
            </section>

            {/* SECTION 8: Data Retention */}
            <section id="sec8" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <History className="sec-icon" />
                </div>
                <h2>8. Data Retention</h2>
              </div>
              <p>
                We retain your information only for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </section>

            {/* SECTION 9: Sharing of Information */}
            <section id="sec9" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Share2 className="sec-icon" />
                </div>
                <h2>9. Sharing of Information</h2>
              </div>
              <p>
                We do not sell, rent, or trade your personal information. Your information may only be shared:
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator"></span>
                  With trusted service providers working on our behalf.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  When required by applicable law.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  To protect our legal rights.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  With your explicit consent.
                </li>
              </ul>
            </section>

            {/* SECTION 10: Your Rights */}
            <section id="sec10" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <ListChecks className="sec-icon" />
                </div>
                <h2>10. Your Rights</h2>
              </div>
              <p>
                You may request to:
              </p>
              <div className="rights-row">
                <div className="right-item-card">
                  <div className="right-badge">01</div>
                  <h4>Access Info</h4>
                  <p>Request copies of all your personal records stored on our servers.</p>
                </div>
                <div className="right-item-card">
                  <div className="right-badge">02</div>
                  <h4>Correct Info</h4>
                  <p>Modify inaccurate, incomplete, or outdated business profiles.</p>
                </div>
                <div className="right-item-card">
                  <div className="right-badge">03</div>
                  <h4>Delete Account</h4>
                  <p>Permanently remove your FastInvoice account registration.</p>
                </div>
                <div className="right-item-card">
                  <div className="right-badge">04</div>
                  <h4>Delete Stored Data</h4>
                  <p>Wipe all cloud databases containing past client data and invoices.</p>
                </div>
              </div>
              <p style={{ marginTop: "20px" }}>
                You may also withdraw consent where applicable.
              </p>
            </section>

            {/* SECTION 11: Children's Privacy */}
            <section id="sec11" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <UserX className="sec-icon" />
                </div>
                <h2>11. Children's Privacy</h2>
              </div>
              <p>
                FastInvoice is not intended for children under the age of 13. We do not knowingly collect personal information from children.
              </p>
            </section>

            {/* SECTION 12: Changes to This Privacy Policy */}
            <section id="sec12" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <RefreshCw className="sec-icon" />
                </div>
                <h2>12. Changes to This Privacy Policy</h2>
              </div>
              <p>
                We may update this Privacy Policy from time to time. Changes will become effective immediately after they are published on our website.
              </p>
            </section>

            {/* SECTION 13: Contact Us */}
            <section id="sec13" className="content-section contact-section-card">
              <div className="section-header">
                <div className="header-icon-box">
                  <Mail className="sec-icon" />
                </div>
                <h2>13. Contact Us</h2>
              </div>
              <p>
                If you have any questions regarding subscriptions, cancellations, billing, or platform terms, please reach out to us:
              </p>

              <div className="contact-details-box">
                <div className="company-meta">
                  <h3>FastInvoice</h3>
                  <p>Invoicing & Inventory Management Solutions</p>
                </div>

                <div className="contact-links-grid">
                  <div className="contact-card-item">
                    <Globe className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Official Website</span>
                      <a href="https://fastinvoice.in" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                        https://fastinvoice.in
                      </a>
                    </div>
                  </div>

                  <div className="contact-card-item">
                    <Mail className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Support Email</span>
                      <div className="email-copy-wrapper">
                        <a href="mailto:support@fastinvoice.in" className="contact-card-link">
                          support@fastinvoice.in
                        </a>
                        <button 
                          className="copy-btn" 
                          onClick={handleCopyEmail}
                          title="Copy Email Address"
                        >
                          {copied ? <CheckCircle size={16} className="copied-icon" /> : <Copy size={16} />}
                          <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </article>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
